import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Shield, UserX, AlertTriangle, MessageSquare, Ban, RefreshCcw, Trash2, Plus, Users, Search, ShieldOff, LogIn, Activity, Undo2, Crown, Download } from "lucide-react";

/**
 * Moderator/Admin/Founder tab: user blacklist CRUD + recent moderation
 * activity (bans, appeals, warnings) + synced Discord members list with
 * ban/unban actions.
 */
export default function ModerationPanel() {
  const [tab, setTab] = useState("members");
  return (
    <div data-testid="moderation-panel">
      <div className="flex items-center gap-3 mb-6">
        <Shield className="text-[var(--ff-red)]" size={22} />
        <div>
          <div className="label-eyebrow text-[var(--ff-red)]">▮ MODERATION</div>
          <h2 className="font-display text-3xl">Moderation Tools</h2>
        </div>
      </div>

      <div className="flex flex-wrap gap-px bg-[var(--ff-border)] mb-6">
        {[
          { id: "members", label: "Members" },
          { id: "blacklist", label: "User Blacklist" },
          { id: "activity", label: "Recent Activity" },
          { id: "radar", label: "Threat Radar" },
          { id: "warn", label: "Warn User" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-3 font-mono-ui text-xs uppercase tracking-[0.15em] ${tab === t.id ? "bg-[var(--ff-red)] text-white" : "bg-[var(--ff-surface)] text-[var(--ff-text-dim)] hover:text-white"}`}
            data-testid={`mod-subtab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "members" && <MembersList />}
      {tab === "blacklist" && <UserBlacklist />}
      {tab === "activity" && <ModActivity />}
      {tab === "radar" && <ThreatRadar />}
      {tab === "warn" && <WarnForm />}
    </div>
  );
}

function MembersList() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | banned | active
  const [modal, setModal] = useState(null); // {user, mode: "ban" | "unban"} or {bulk: true, ids: [...]}
  const [selected, setSelected] = useState(new Set());

  const load = async () => {
    try {
      const { data } = await api.get("/admin/users-with-guilds");
      setRows(data);
      setSelected(new Set());
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load members");
    }
  };
  useEffect(() => { load(); }, []);

  const [syncing, setSyncing] = useState(false);
  const syncDiscordBans = async () => {
    if (!window.confirm("Fetch the current Discord ban list and mark every matched user as banned on the website? This won't ban anyone who isn't already banned on Discord.")) return;
    setSyncing(true);
    try {
      const { data } = await api.post("/mod/sync-discord-bans");
      const parts = [
        `${data.newly_banned} newly synced`,
        `${data.already_banned} already banned`,
        `${data.unlinked} on Discord w/ no site account`,
      ];
      if (data.skipped_protected) parts.push(`${data.skipped_protected} skipped (protected role)`);
      toast.success(`Sync complete · ${data.fetched} Discord bans · ${parts.join(" · ")}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Discord sync failed");
    } finally { setSyncing(false); }
  };

  const filtered = React.useMemo(() => {
    if (!rows) return [];
    const needle = q.trim().toLowerCase();
    return rows.filter((u) => {
      if (filter === "banned" && !u.is_banned) return false;
      if (filter === "active" && u.is_banned) return false;
      if (!needle) return true;
      return (u.discord_username || "").toLowerCase().includes(needle)
          || (u.discord_id || "").includes(needle);
    });
  }, [rows, q, filter]);

  const isProtected = (u) => ["founder","admin","moderator"].includes(u.role);
  const selectableIds = filtered.filter((u) => !isProtected(u)).map((u) => u.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(() => allSelected ? new Set() : new Set(selectableIds));

  // Split selection by ban state so we can offer the right bulk action.
  const selectedRows = React.useMemo(() => (rows || []).filter((r) => selected.has(r.id)), [rows, selected]);
  const selectedActive = selectedRows.filter((r) => !r.is_banned);
  const selectedBanned = selectedRows.filter((r) => r.is_banned);

  return (
    <div data-testid="mod-members">
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ff-text-dim)]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by username or Discord ID…"
            className="w-full pl-9 pr-3 py-2.5 text-sm" data-testid="mod-members-search" />
        </div>
        <div className="flex gap-px bg-[var(--ff-border)]">
          {[["all", "All"], ["active", "Active"], ["banned", "Banned"]].map(([id, label]) => (
            <button key={id} onClick={() => setFilter(id)}
              className={`px-3 py-2.5 font-mono-ui text-xs uppercase tracking-[0.15em] ${filter === id ? "bg-[var(--ff-red)] text-white" : "bg-[var(--ff-surface)] text-[var(--ff-text-dim)] hover:text-white"}`}
              data-testid={`mod-members-filter-${id}`}>{label}</button>
          ))}
        </div>
        <button onClick={load} className="btn-ghost text-xs" data-testid="mod-members-refresh"><RefreshCcw size={12} /> Refresh</button>
        <button onClick={syncDiscordBans} disabled={syncing} className="btn-ghost text-xs" data-testid="mod-members-sync-discord-bans"
          title="Fetch the current Discord guild ban list and mark every matched user as banned on the website">
          <Download size={12} /> {syncing ? "Syncing…" : "Sync Discord Bans"}
        </button>
      </div>
      {!rows && <div className="text-[var(--ff-text-dim)] font-mono-ui text-sm">Loading members…</div>}
      {rows && (
        <div className="surface-card overflow-hidden">
          <div className="hidden md:grid grid-cols-[36px_minmax(0,3fr)_minmax(0,2fr)_100px_140px] gap-4 px-4 py-3 border-b border-[var(--ff-border)] font-mono-ui text-[0.6rem] uppercase tracking-[0.2em] text-[var(--ff-text-dim)] items-center">
            <div>
              <input type="checkbox" checked={allSelected} onChange={toggleAll}
                     disabled={selectableIds.length === 0}
                     className="w-4 h-4 accent-[var(--ff-red)]"
                     data-testid="mod-members-select-all"
                     title={selectableIds.length ? "Select every bannable user in this view" : "Nothing to select"} />
            </div>
            <div>User · <Users size={11} className="inline" /> {rows.length} synced · {selectableIds.length} selectable in view</div>
            <div>Role</div>
            <div>Status</div>
            <div className="text-right">Action</div>
          </div>
          <div className="divide-y divide-[var(--ff-border)] max-h-[560px] overflow-auto">
            {filtered.map((u) => {
              const protectd = isProtected(u);
              return (
                <div key={u.id} className={`grid md:grid-cols-[36px_minmax(0,3fr)_minmax(0,2fr)_100px_140px] gap-4 px-4 py-2.5 items-center ${selected.has(u.id) ? "bg-[#2A1010]/40" : ""}`} data-testid={`mod-member-row-${u.id}`}>
                  <div>
                    <input type="checkbox" disabled={protectd}
                      checked={selected.has(u.id)} onChange={() => toggle(u.id)}
                      className="w-4 h-4 accent-[var(--ff-red)]"
                      data-testid={`mod-member-check-${u.id}`}
                      title={protectd ? "Protected role or already banned" : ""} />
                  </div>
                  <div className="flex items-center gap-3 min-w-0">
                    {u.discord_avatar
                      ? <img src={u.discord_avatar} alt="" className="w-8 h-8 rounded-full shrink-0" />
                      : <div className="w-8 h-8 rounded-full bg-[var(--ff-red)] text-white flex items-center justify-center text-[0.6rem]">?</div>}
                    <div className="min-w-0">
                      <div className="text-sm truncate flex items-center gap-2">
                        {u.discord_username || "—"}
                        {u.is_founding_member && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0 border border-amber-400 bg-amber-400/10 text-amber-300 text-[0.5rem] uppercase tracking-[0.12em] font-mono-ui"
                            title={`Founding Member #${u.founding_member_number}`}
                            data-testid={`mod-member-founder-${u.id}`}
                          >
                            <Crown size={9} /> #{u.founding_member_number}
                          </span>
                        )}
                      </div>
                      <div className="text-[0.55rem] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)] truncate">ID {u.discord_id}</div>
                    </div>
                  </div>
                  <div className="font-mono-ui text-[0.6rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)]">
                    {u.role} · {(u.roles || []).filter((r) => r !== u.role).join(" · ") || "—"}
                  </div>
                  <div>
                    {u.is_banned ? (
                      <span className="px-2 py-0.5 border border-red-500/50 text-red-300 text-[0.55rem] uppercase tracking-[0.15em] font-mono-ui">Banned</span>
                    ) : (
                      <span className="px-2 py-0.5 border border-green-500/50 text-green-300 text-[0.55rem] uppercase tracking-[0.15em] font-mono-ui">Active</span>
                    )}
                  </div>
                  <div className="flex justify-end gap-2">
                    {u.is_banned ? (
                      <button onClick={() => setModal({ user: u, mode: "unban" })} className="btn-ghost text-xs" data-testid={`mod-unban-open-${u.id}`}>
                        <ShieldOff size={12} /> Unban
                      </button>
                    ) : (
                      <button onClick={() => setModal({ user: u, mode: "ban" })} className="btn-primary text-xs" data-testid={`mod-ban-open-${u.id}`}
                        disabled={["founder","admin","moderator"].includes(u.role)}>
                        <Ban size={12} /> Ban
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div className="px-4 py-8 text-center text-[var(--ff-text-dim)] font-mono-ui text-sm">
                No members match this filter.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sticky bulk action bar */}
      {selected.size > 0 && (
        <div className="sticky bottom-4 mt-4 z-40" data-testid="mod-bulk-bar">
          <div className="surface-card p-4 flex flex-col md:flex-row items-center gap-4 justify-between border-[var(--ff-red)]">
            <div className="font-mono-ui text-xs uppercase tracking-[0.15em]">
              <span className="text-[var(--ff-red)] font-bold">{selected.size} selected</span>
              {selectedActive.length > 0 && selectedBanned.length > 0 && (
                <span className="text-[var(--ff-text-dim)]"> · {selectedActive.length} active + {selectedBanned.length} banned</span>
              )}
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <button onClick={() => setSelected(new Set())} className="btn-ghost text-xs" data-testid="mod-bulk-clear">
                Clear
              </button>
              {selectedActive.length > 0 && (
                <button
                  onClick={() => setModal({ bulk: "ban", ids: selectedActive.map((r) => r.id) })}
                  className="btn-primary text-xs"
                  data-testid="mod-bulk-ban-open"
                >
                  <Ban size={12} /> Bulk Ban {selectedActive.length}
                </button>
              )}
              {selectedBanned.length > 0 && (
                <button
                  onClick={() => setModal({ bulk: "unban", ids: selectedBanned.map((r) => r.id) })}
                  className="btn-ghost text-xs"
                  data-testid="mod-bulk-unban-open"
                >
                  <ShieldOff size={12} /> Bulk Unban {selectedBanned.length}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {modal && !modal.bulk && <ActionModal ctx={modal} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />}
      {modal && modal.bulk === "ban" && (
        <BulkBanModal ids={modal.ids} rows={rows || []}
          onClose={() => setModal(null)}
          onDone={() => { setModal(null); load(); }} />
      )}
      {modal && modal.bulk === "unban" && (
        <BulkUnbanModal ids={modal.ids} rows={rows || []}
          onClose={() => setModal(null)}
          onDone={() => { setModal(null); load(); }} />
      )}
    </div>
  );
}

function BulkUnbanModal({ ids, rows, onClose, onDone }) {
  const [mode, setMode] = useState("rejoin");
  const [busy, setBusy] = useState(false);
  const targets = rows.filter((r) => ids.includes(r.id));
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/mod/bulk-unban", { user_ids: ids, mode });
      const rejoinNote = mode === "rejoin" ? ` · ${data.rejoined || 0} auto-rejoined` : "";
      toast.success(`Bulk unban complete · ${data.unbanned}/${data.requested} unbanned${rejoinNote}`);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6" onClick={onClose} data-testid="mod-bulk-unban-modal">
      <div onClick={(e) => e.stopPropagation()} className="surface-card p-6 max-w-lg w-full">
        <div className="flex items-center gap-3 mb-4 pb-4 border-b border-[var(--ff-border)]">
          <ShieldOff size={28} className="text-green-400" />
          <div>
            <div className="label-eyebrow text-green-400">▮ BULK UNBAN</div>
            <div className="font-display text-xl">{ids.length} banned users selected</div>
          </div>
        </div>

        <div className="max-h-32 overflow-auto border border-[var(--ff-border)] p-2 mb-4 text-sm">
          {targets.slice(0, 6).map((t) => (
            <div key={t.id} className="flex items-center gap-2 py-0.5" data-testid={`bulk-unban-preview-${t.id}`}>
              {t.discord_avatar
                ? <img src={t.discord_avatar} alt="" className="w-5 h-5 rounded-full" />
                : <div className="w-5 h-5 rounded-full bg-[var(--ff-red)]" />}
              <span className="truncate">{t.discord_username}</span>
              <span className="text-[0.6rem] font-mono-ui text-[var(--ff-text-dim)] ml-auto">{t.discord_id}</span>
            </div>
          ))}
          {targets.length > 6 && (
            <div className="text-[0.65rem] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)] pt-1">
              + {targets.length - 6} more…
            </div>
          )}
        </div>

        <div className="label-eyebrow mb-3 text-green-400">▮ CHOOSE UNBAN TYPE</div>
        <div className="space-y-2 mb-4">
          <ChoiceCard checked={mode === "regular"} onSelect={() => setMode("regular")}
            title="Regular Unban"
            desc="Clears the local ban + lifts the Discord guild ban for each user. They can rejoin manually. Any who were on the user blacklist are removed."
            icon={<ShieldOff size={14} />} testId="bulk-unban-mode-regular" />
          <ChoiceCard checked={mode === "rejoin"} onSelect={() => setMode("rejoin")}
            title="Unban & Auto-Rejoin"
            desc="All of the above PLUS auto-adds each user back to Discord with the Members role using their stored OAuth refresh token. Users without a valid token are unbanned but won't be auto-rejoined."
            icon={<LogIn size={14} />} testId="bulk-unban-mode-rejoin" />
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-[var(--ff-border)]">
          <button onClick={onClose} className="btn-ghost text-xs" data-testid="mod-bulk-unban-cancel">Cancel</button>
          <button onClick={submit} disabled={busy} className="btn-primary" data-testid="mod-bulk-unban-confirm">
            <ShieldOff size={13} /> {busy ? `Unbanning ${ids.length}…` : `Unban ${ids.length} Users`}
          </button>
        </div>
      </div>
    </div>
  );
}

function BulkBanModal({ ids, rows, onClose, onDone }) {
  const [mode, setMode] = useState("regular");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const targets = rows.filter((r) => ids.includes(r.id));
  const submit = async () => {
    if (!reason.trim()) return toast.error("Reason required");
    setBusy(true);
    try {
      const { data } = await api.post("/mod/bulk-ban", { user_ids: ids, reason: reason.trim(), mode });
      const adj = data.adjacency_scan?.newly_banned;
      toast.success(`Bulk ban complete · ${data.banned}/${data.requested} banned · ${data.skipped} skipped${adj ? ` · ${adj} adjacency bans` : ""}`);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6" onClick={onClose} data-testid="mod-bulk-modal">
      <div onClick={(e) => e.stopPropagation()} className="surface-card p-6 max-w-lg w-full">
        <div className="flex items-center gap-3 mb-4 pb-4 border-b border-[var(--ff-border)]">
          <Ban size={28} className="text-[var(--ff-red)]" />
          <div>
            <div className="label-eyebrow text-[var(--ff-red)]">▮ BULK BAN</div>
            <div className="font-display text-xl">{ids.length} users selected</div>
          </div>
        </div>

        {/* Preview list of targets, capped at 6 with a "+N more" note */}
        <div className="max-h-32 overflow-auto border border-[var(--ff-border)] p-2 mb-4 text-sm">
          {targets.slice(0, 6).map((t) => (
            <div key={t.id} className="flex items-center gap-2 py-0.5" data-testid={`bulk-preview-${t.id}`}>
              {t.discord_avatar
                ? <img src={t.discord_avatar} alt="" className="w-5 h-5 rounded-full" />
                : <div className="w-5 h-5 rounded-full bg-[var(--ff-red)]" />}
              <span className="truncate">{t.discord_username}</span>
              <span className="text-[0.6rem] font-mono-ui text-[var(--ff-text-dim)] ml-auto">{t.discord_id}</span>
            </div>
          ))}
          {targets.length > 6 && (
            <div className="text-[0.65rem] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)] pt-1">
              + {targets.length - 6} more…
            </div>
          )}
        </div>

        <div className="label-eyebrow mb-3 text-[var(--ff-red)]">▮ CHOOSE BAN TYPE</div>
        <div className="space-y-2 mb-4">
          <ChoiceCard checked={mode === "regular"} onSelect={() => setMode("regular")}
            title="Regular Ban" desc="Guild ban + local ban + DM with appeal link for each user."
            icon={<Ban size={14} />} testId="bulk-mode-regular" />
          <ChoiceCard checked={mode === "blacklist"} onSelect={() => setMode("blacklist")}
            title="Ban & Blacklist"
            desc="All of the above PLUS adds every user to the blacklist. A single adjacency scan runs at the end — anyone sharing a guild with these users gets auto-banned too."
            icon={<UserX size={14} />} testId="bulk-mode-blacklist" />
        </div>

        <div className="label-eyebrow mb-2">Reason (every user sees this in their DM)</div>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
          placeholder="e.g. Coordinated raid on 2026-08-17" className="w-full px-3 py-2 text-sm mb-4" data-testid="bulk-reason-input" />

        <div className="flex justify-end gap-3 pt-4 border-t border-[var(--ff-border)]">
          <button onClick={onClose} className="btn-ghost text-xs" data-testid="mod-bulk-cancel">Cancel</button>
          <button onClick={submit} disabled={busy} className="btn-primary" data-testid="mod-bulk-confirm">
            <Ban size={13} /> {busy ? `Banning ${ids.length}…` : `Ban ${ids.length} Users`}
          </button>
        </div>
      </div>
    </div>
  );
}

function ActionModal({ ctx, onClose, onDone }) {
  const { user, mode } = ctx;
  const [banMode, setBanMode] = useState("regular");
  const [unbanMode, setUnbanMode] = useState("rejoin");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      if (mode === "ban") {
        if (!reason.trim()) { toast.error("Reason required"); setBusy(false); return; }
        const { data } = await api.post("/mod/ban", { user_id: user.id, reason: reason.trim(), mode: banMode });
        const adj = data.adjacency_scan?.newly_banned;
        toast.success(`Banned · ${banMode === "blacklist" ? "blacklisted + " : ""}Discord status ${data.discord_ban_status?.status || "n/a"}${adj ? ` · ${adj} adjacency bans` : ""}`);
      } else {
        const { data } = await api.post("/mod/unban", { user_id: user.id, mode: unbanMode });
        toast.success(`Unbanned · Discord ${data.discord_unban_status || "n/a"}${data.rejoin?.ok ? " · rejoined" : (unbanMode === "rejoin" ? " · rejoin failed" : "")}`);
      }
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6" onClick={onClose} data-testid="mod-action-modal">
      <div onClick={(e) => e.stopPropagation()} className="surface-card p-6 max-w-md w-full">
        <div className="flex items-center gap-3 mb-4 pb-4 border-b border-[var(--ff-border)]">
          {user.discord_avatar
            ? <img src={user.discord_avatar} alt="" className="w-12 h-12 rounded-full" />
            : <div className="w-12 h-12 rounded-full bg-[var(--ff-red)] text-white flex items-center justify-center">?</div>}
          <div className="min-w-0">
            <div className="font-display text-lg truncate">{user.discord_username}</div>
            <div className="text-[0.6rem] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)]">ID {user.discord_id}</div>
          </div>
        </div>

        {mode === "ban" ? (
          <>
            <div className="label-eyebrow mb-3 text-[var(--ff-red)]">▮ CHOOSE BAN TYPE</div>
            <div className="space-y-2 mb-4">
              <ChoiceCard checked={banMode === "regular"} onSelect={() => setBanMode("regular")}
                title="Regular Ban" desc="Guild ban + local ban + DM with appeal link."
                icon={<Ban size={14} />} testId="ban-mode-regular" />
              <ChoiceCard checked={banMode === "blacklist"} onSelect={() => setBanMode("blacklist")}
                title="Ban & Blacklist" desc="Everything above PLUS adds the user to the blacklist. Adjacency rule bans anyone sharing a guild with them."
                icon={<UserX size={14} />} testId="ban-mode-blacklist" />
            </div>
            <div className="label-eyebrow mb-2">Reason (they&apos;ll see this)</div>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
              placeholder="e.g. Repeated harassment of hosts" className="w-full px-3 py-2 text-sm" data-testid="ban-reason-input" />
          </>
        ) : (
          <>
            <div className="label-eyebrow mb-2 text-[var(--ff-text-dim)]">CURRENT BAN REASON</div>
            <div className="text-sm mb-4 font-mono-ui text-[var(--ff-text-dim)]">{user.ban_reason || "—"}</div>
            <div className="label-eyebrow mb-3 text-[var(--ff-red)]">▮ CHOOSE UNBAN TYPE</div>
            <div className="space-y-2">
              <ChoiceCard checked={unbanMode === "regular"} onSelect={() => setUnbanMode("regular")}
                title="Regular Unban" desc="Clears the local ban + lifts the Discord guild ban. User can rejoin manually."
                icon={<ShieldOff size={14} />} testId="unban-mode-regular" />
              <ChoiceCard checked={unbanMode === "rejoin"} onSelect={() => setUnbanMode("rejoin")}
                title="Unban & Auto-Rejoin" desc="Everything above PLUS auto-adds them back into Discord with the Members role using their stored OAuth token."
                icon={<LogIn size={14} />} testId="unban-mode-rejoin" />
            </div>
          </>
        )}

        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-[var(--ff-border)]">
          <button onClick={onClose} className="btn-ghost text-xs" data-testid="mod-action-cancel">Cancel</button>
          <button onClick={submit} disabled={busy} className={mode === "ban" ? "btn-primary" : "btn-primary"} data-testid="mod-action-confirm">
            {mode === "ban" ? <><Ban size={13} /> Confirm Ban</> : <><ShieldOff size={13} /> Confirm Unban</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function ChoiceCard({ checked, onSelect, title, desc, icon, testId }) {
  return (
    <button onClick={onSelect} type="button" data-testid={testId}
      className={`w-full text-left p-3 border transition-colors ${checked ? "border-[var(--ff-red)] bg-[#2A1010]" : "border-[var(--ff-border)] hover:border-[var(--ff-text-dim)]"}`}>
      <div className="flex items-center gap-2 mb-1 font-display text-sm">
        <span className={checked ? "text-[var(--ff-red)]" : "text-[var(--ff-text-dim)]"}>{icon}</span>
        {title}
        {checked && <span className="text-[0.55rem] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-red)] ml-auto">SELECTED</span>}
      </div>
      <div className="text-[0.7rem] text-[var(--ff-text-dim)] leading-relaxed">{desc}</div>
    </button>
  );
}

function UserBlacklist() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ discord_id: "", username: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try { setRows((await api.get("/admin/blacklisted-users")).data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Load failed"); }
  };
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    if (!form.discord_id.trim()) return toast.error("Discord ID required");
    setBusy(true);
    try {
      await api.post("/admin/blacklisted-users", form);
      toast.success("Added to blacklist");
      setForm({ discord_id: "", username: "", reason: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove from blacklist? Users banned due to adjacency to this user will be unbanned.")) return;
    try { await api.delete(`/admin/blacklisted-users/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  return (
    <div className="grid md:grid-cols-[minmax(0,1fr)_360px] gap-6" data-testid="user-blacklist">
      <div className="surface-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="label-eyebrow flex items-center gap-2"><UserX size={12} /> ▮ BLACKLISTED DISCORD USERS · {rows.length}</div>
          <button onClick={load} className="btn-ghost text-xs" data-testid="user-blacklist-refresh"><RefreshCcw size={12} /> Refresh</button>
        </div>
        <div className="text-[var(--ff-text-dim)] text-xs font-mono-ui uppercase tracking-[0.15em] mb-4">
          Anyone sharing a Discord server with these users is auto-banned. Removing an entry lifts adjacency bans it caused.
        </div>
        {rows.length === 0 && (
          <div className="text-[var(--ff-text-dim)] font-mono-ui text-sm py-6 text-center">No users blacklisted yet.</div>
        )}
        {rows.map((r) => (
          <div key={r.discord_id} className="flex items-center justify-between border-t border-[var(--ff-border)] py-3 gap-3" data-testid={`bl-user-${r.discord_id}`}>
            <div className="min-w-0">
              <div className="text-sm truncate">{r.username || "(unknown username)"}</div>
              <div className="text-[0.6rem] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)]">ID {r.discord_id}</div>
              {r.reason && <div className="text-xs text-[var(--ff-text-dim)] mt-1 truncate">{r.reason}</div>}
            </div>
            <button onClick={() => remove(r.discord_id)} className="btn-ghost text-xs" data-testid={`bl-user-remove-${r.discord_id}`}>
              <Trash2 size={12} /> Remove
            </button>
          </div>
        ))}
      </div>

      <form onSubmit={add} className="surface-card p-6 space-y-3" data-testid="user-blacklist-add-form">
        <div className="label-eyebrow flex items-center gap-2"><Plus size={12} /> ▮ ADD USER</div>
        <input value={form.discord_id} onChange={(e) => setForm({ ...form, discord_id: e.target.value })} placeholder="Discord ID (right-click user → Copy User ID)" className="w-full px-3 py-2 text-sm" data-testid="bl-user-id-input" />
        <input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="Username (for reference)" className="w-full px-3 py-2 text-sm" data-testid="bl-user-name-input" />
        <textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Reason (e.g. serial harasser)" rows={3} className="w-full px-3 py-2 text-sm" data-testid="bl-user-reason-input" />
        <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="bl-user-add-btn">
          <Ban size={13} /> {busy ? "Adding…" : "Add to Blacklist"}
        </button>
      </form>
    </div>
  );
}

function ModActivity() {
  const [data, setData] = useState(null);
  const [undoing, setUndoing] = useState(null);
  const load = () => api.get("/mod/actions").then((r) => setData(r.data)).catch(() => setData({actions:[], recent_bans:[], recent_appeals:[]}));
  useEffect(() => { load(); }, []);
  if (!data) return <div className="text-[var(--ff-text-dim)] text-sm">Loading…</div>;

  // Group bulk bans by batch: same actor + at ±60s → one row with an Undo button.
  const bulkBans = (data.actions || []).filter((a) => a.kind === "ban" && a.bulk);
  const batches = [];
  const seen = new Set();
  for (const a of bulkBans) {
    if (seen.has(a.id)) continue;
    const at = new Date(a.at || 0).getTime();
    const peers = bulkBans.filter((b) => b.by === a.by && Math.abs(new Date(b.at || 0).getTime() - at) <= 60_000);
    peers.forEach((p) => seen.add(p.id));
    batches.push({ anchor: a, count: peers.length, peers });
  }

  const undoBatch = async (anchorId) => {
    if (!window.confirm("Undo this entire bulk ban? All users in the batch will be unbanned (regular unban).")) return;
    setUndoing(anchorId);
    try {
      const { data: r } = await api.post("/mod/undo-bulk-ban", { action_id: anchorId });
      toast.success(`Undo complete · ${r.unban_result?.unbanned || 0}/${r.batch_size || 0} unbanned`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Undo failed");
    } finally { setUndoing(null); }
  };

  return (
    <div className="space-y-6" data-testid="mod-activity">
      <Section title="BULK BAN BATCHES · UNDOABLE" empty="No bulk bans yet." icon={<Undo2 size={12} />}>
        {batches.slice(0, 10).map((b) => (
          <div key={b.anchor.id} className="border-t border-[var(--ff-border)] py-2.5 text-sm flex items-center justify-between gap-3" data-testid={`bulk-batch-${b.anchor.id}`}>
            <div className="min-w-0">
              <div className="truncate">
                <span className="text-[var(--ff-red)] font-bold">{b.count}</span> user{b.count === 1 ? "" : "s"} banned by <span className="text-white">{b.anchor.by}</span>
              </div>
              <div className="text-[0.6rem] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)]">
                {(b.anchor.at || "").slice(0, 16).replace("T", " ")} · reason: {(b.anchor.reason || "—").slice(0, 60)}
              </div>
            </div>
            <button
              onClick={() => undoBatch(b.anchor.id)}
              disabled={undoing === b.anchor.id}
              className="btn-ghost text-xs whitespace-nowrap"
              data-testid={`bulk-batch-undo-${b.anchor.id}`}
            >
              <Undo2 size={12} /> {undoing === b.anchor.id ? "Undoing…" : "Undo Batch"}
            </button>
          </div>
        ))}
      </Section>

      <Section title="RECENT BANS" empty="No auto-bans yet." icon={<Ban size={12} />}>
        {data.recent_bans.slice(0, 15).map((b) => (
          <div key={b.id} className="border-t border-[var(--ff-border)] py-2.5 text-sm" data-testid={`ban-row-${b.id}`}>
            <div className="flex justify-between items-start gap-3">
              <div className="min-w-0">
                <div className="truncate">{b.discord_username || b.discord_id}</div>
                <div className="text-[0.6rem] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)] truncate">{b.reason}</div>
              </div>
              <div className="text-[0.6rem] font-mono-ui text-[var(--ff-text-dim)] whitespace-nowrap">{(b.at || "").slice(0, 10)}</div>
            </div>
          </div>
        ))}
      </Section>
      <Section title="RECENT APPEALS" empty="No appeals yet." icon={<MessageSquare size={12} />}>
        {data.recent_appeals.slice(0, 15).map((a) => (
          <div key={a.id} className="border-t border-[var(--ff-border)] py-2.5 text-sm" data-testid={`appeal-row-${a.id}`}>
            <div className="flex justify-between items-start gap-3">
              <div className="min-w-0">
                <div className="truncate">{a.discord_username || a.user_id}</div>
                <div className="text-[0.6rem] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)]">
                  {a.reviewed ? `${a.decision || "reviewed"}` : "pending"} · {(a.submitted_at || "").slice(0, 10)}
                </div>
              </div>
            </div>
          </div>
        ))}
      </Section>
      <Section title="WARNINGS" empty="No warnings issued." icon={<AlertTriangle size={12} />}>
        {(data.actions || []).filter((a) => a.kind === "warn").slice(0, 15).map((a) => (
          <div key={a.id} className="border-t border-[var(--ff-border)] py-2.5 text-sm" data-testid={`warn-row-${a.id}`}>
            <div className="truncate">{a.target_username || a.target_discord_id}</div>
            <div className="text-[0.6rem] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)]">
              {a.reason} · by {a.by} · {(a.at || "").slice(0, 10)}
            </div>
          </div>
        ))}
      </Section>
    </div>
  );
}

function ThreatRadar() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const load = () => {
    setErr(null);
    api.get("/mod/threat-radar")
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatApiError(e.response?.data?.detail) || "Failed to load radar"));
  };
  useEffect(() => { load(); }, []);
  if (err) return <div className="surface-card p-6 text-red-300 font-mono-ui text-sm" data-testid="threat-radar-error">{err}</div>;
  if (!data) return <div className="text-[var(--ff-text-dim)] text-sm">Loading radar…</div>;

  return (
    <div className="space-y-6" data-testid="threat-radar">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-[var(--ff-border)]">
        <StatBoxRadar label="Bans this month" value={data.total_bans_this_month} />
        <StatBoxRadar label="Bans all-time" value={data.total_bans_all_time} />
        <StatBoxRadar label="Tracked sources" value={data.entries?.length || 0} />
      </div>

      <div className="surface-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="label-eyebrow flex items-center gap-2"><Activity size={12} /> ▮ TOP THREAT SOURCES</div>
          <button onClick={load} className="btn-ghost text-xs" data-testid="threat-radar-refresh">
            <RefreshCcw size={12} /> Refresh
          </button>
        </div>
        <div className="text-[var(--ff-text-dim)] text-xs font-mono-ui uppercase tracking-[0.15em] mb-4">
          Which blacklist entries — servers, direct user bans, adjacency — have caused the most bans. Ranked by &quot;this month&quot; then all-time.
        </div>
        {(data.entries || []).length === 0 && (
          <div className="text-[var(--ff-text-dim)] font-mono-ui text-sm py-6 text-center">
            No bans logged yet — the radar has nothing to show.
          </div>
        )}
        <div className="divide-y divide-[var(--ff-border)]">
          {(data.entries || []).map((e) => (
            <div key={`${e.kind}:${e.key}`} className="py-3 grid grid-cols-[minmax(0,1fr)_80px_80px_120px] gap-3 items-center" data-testid={`threat-radar-row-${e.kind}-${e.key}`}>
              <div className="min-w-0">
                <div className="text-sm truncate">{e.label}</div>
                <div className="text-[0.6rem] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)]">
                  {e.kind}{e.kind === "guild" ? ` · ${e.key}` : ""}
                </div>
              </div>
              <div className="text-center">
                <div className="font-display text-lg text-[var(--ff-red)]">{e.this_month}</div>
                <div className="text-[0.55rem] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)]">this mo</div>
              </div>
              <div className="text-center">
                <div className="font-display text-lg">{e.all_time}</div>
                <div className="text-[0.55rem] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)]">all-time</div>
              </div>
              <div className="text-right text-[0.65rem] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)]">
                {(e.last_at || "").slice(0, 10) || "—"}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatBoxRadar({ label, value }) {
  return (
    <div className="bg-[var(--ff-surface)] p-4">
      <div className="label-eyebrow text-[var(--ff-text-dim)] mb-1">{label}</div>
      <div className="font-display text-2xl">{value ?? 0}</div>
    </div>
  );
}

function Section({ title, empty, icon, children }) {
  const kids = React.Children.toArray(children);
  return (
    <div className="surface-card p-6">
      <div className="label-eyebrow mb-3 flex items-center gap-2">{icon} ▮ {title}</div>
      {kids.length ? kids : <div className="text-[var(--ff-text-dim)] font-mono-ui text-sm py-4 text-center">{empty}</div>}
    </div>
  );
}

function WarnForm() {
  const [form, setForm] = useState({ user_id: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/mod/warn", form); toast.success("Warning issued & DM sent"); setForm({ user_id: "", reason: "" }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="surface-card p-6 space-y-3 max-w-2xl" data-testid="warn-form">
      <div className="label-eyebrow flex items-center gap-2"><AlertTriangle size={12} /> ▮ ISSUE WARNING</div>
      <input value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} placeholder="User ID (from /admin/users-with-guilds)" className="w-full px-3 py-2 text-sm" data-testid="warn-user-id" />
      <textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Reason (they'll see this in their DM)" rows={4} className="w-full px-3 py-2 text-sm" data-testid="warn-reason" />
      <button type="submit" disabled={busy || !form.user_id || !form.reason} className="btn-primary" data-testid="warn-submit">
        <AlertTriangle size={13} /> {busy ? "Sending…" : "Send Warning"}
      </button>
    </form>
  );
}
